import { useEffect, useState } from "react";
import { Card, Pill, fmt } from "../components/ui";
import { api, type Pace } from "../lib/api";

const PRESET_LABELS: Record<string, string> = { slow: "Slow", normal: "Normal", fast: "Fast" };

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
  });
const hourLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric" });

/** How fast texts leave, for everyone at once. Replaces editing the six
 *  send jobs by hand: the number set here is what every job shares, and it
 *  applies from the next minute. */
export function SendingSpeed({ canChange, onError }: { canChange: boolean; onError: (m: string) => void }) {
  const [pace, setPace] = useState<Pace | null>(null);
  const [choice, setChoice] = useState<number | null>(null);
  const [preview, setPreview] = useState<Pace | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .pace()
      .then((p) => {
        setPace(p);
        setChoice(null);
        setPreview(null);
      })
      .catch((e) => onError(e.message));

  useEffect(() => {
    load();
    const t = setInterval(() => api.pace().then(setPace).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, []);

  // What the chosen speed would do, before anyone saves it.
  useEffect(() => {
    if (choice == null || !pace || choice === pace.texts_per_minute) return setPreview(null);
    if (choice < pace.min || choice > pace.max) return setPreview(null);
    const t = setTimeout(() => api.pace(choice).then(setPreview).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [choice, pace?.texts_per_minute]);

  if (!pace) return null;

  const current = pace.texts_per_minute;
  const changed = choice != null && choice !== current;
  const valid = choice != null && choice >= pace.min && choice <= pace.max;
  const shown = changed && valid && preview ? preview : pace;
  const top = Math.max(current, ...pace.recent.map((m) => m.sent), 1);

  const save = () => {
    if (choice == null) return;
    const slower = choice < current;
    if (!confirm(`Send ${choice} texts a minute instead of ${current}?\n\nThis applies to every campaign, blast and reminder from the next minute.${slower ? "" : "\nReplies and missed calls will come back faster too."}`)) return;
    setSaving(true);
    api
      .updateSendConfig({ texts_per_minute: choice })
      .then(load)
      .catch((e) => onError(e.message))
      .finally(() => setSaving(false));
  };

  return (
    <Card
      title="Sending speed"
      actions={
        pace.paused ? (
          <Pill tone="ba">sending is stopped</Pill>
        ) : (
          <Pill tone="ok">{current} a minute</Pill>
        )
      }
      footer={
        <span className="dim">
          One speed for everything — campaigns, blasts and appointment reminders together, with reminders
          always first. A change applies from the next minute. Texts go {pace.window.opens}–{pace.window.closes} ET;
          whatever is not reached by the close goes on the next sending day.
        </span>
      }
    >
      <div className="col" style={{ gap: 16 }}>
        <div className="row w" style={{ gap: 28, alignItems: "flex-end" }}>
          <div className="col" style={{ gap: 2 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>Set to</span>
            <span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
              {current} <span style={{ fontSize: 13, fontWeight: 500 }}>texts a minute</span>
            </span>
          </div>
          <div className="col" style={{ gap: 2 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>Actually sent, last 5 minutes</span>
            <span style={{ fontSize: 18, fontWeight: 600 }}>{fmt.n(pace.last_5_minutes_average)} a minute</span>
          </div>
          <div className="col" style={{ gap: 4 }} title="Texts sent in each of the last 15 minutes">
            <span className="dim" style={{ fontSize: 11.5 }}>Last 15 minutes</span>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 36, position: "relative" }}>
              <div
                style={{
                  position: "absolute", left: 0, right: 0, bottom: `${(current / top) * 36}px`,
                  borderTop: "1px dashed var(--ink3)",
                }}
              />
              {pace.recent.map((m) => (
                <div
                  key={m.minute}
                  title={`${clock(m.minute)} — ${m.sent} sent`}
                  style={{
                    width: 7, height: Math.max(1, (m.sent / top) * 36),
                    background: "var(--acc)", opacity: m.sent ? 0.85 : 0.25, borderRadius: 1,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="row w" style={{ gap: 8 }}>
          {Object.entries(pace.presets).map(([key, n]) => (
            <button
              key={key}
              className={`btn s${(choice ?? current) === n ? " p" : ""}`}
              disabled={!canChange}
              onClick={() => setChoice(n)}
            >
              {PRESET_LABELS[key] ?? key} · {n}
            </button>
          ))}
          <span className="dim" style={{ fontSize: 12 }}>or</span>
          <input
            className="f"
            type="number"
            min={pace.min}
            max={pace.max}
            step={10}
            style={{ width: 90 }}
            disabled={!canChange}
            value={choice ?? current}
            onChange={(e) => setChoice(e.target.value === "" ? null : Number(e.target.value))}
          />
          <span className="dim" style={{ fontSize: 12 }}>a minute ({pace.min}–{pace.max})</span>
          <div className="sp" />
          {canChange && changed && (
            <>
              <button className="btn s" onClick={() => { setChoice(null); setPreview(null); }}>Cancel</button>
              <button className="btn p" disabled={!valid || saving} onClick={save}>
                {saving ? "Saving…" : `Set to ${choice} a minute`}
              </button>
            </>
          )}
        </div>
        {!canChange && (
          <span className="dim" style={{ fontSize: 12 }}>Managers and admins can change the speed.</span>
        )}
        {changed && !valid && (
          <span style={{ fontSize: 12.5, color: "var(--bad)" }}>
            Between {pace.min} and {pace.max} a minute. {pace.max} is the fastest this system has been run at.
          </span>
        )}

        <Outlook pace={shown} previewing={changed && valid && preview != null} />
      </div>
    </Card>
  );
}

function Outlook({ pace, previewing }: { pace: Pace; previewing: boolean }) {
  const f = pace.forecast;
  const at = pace.forecast_at;
  return (
    <div
      className="col"
      style={{
        gap: 6, padding: "10px 12px", borderRadius: 6,
        background: previewing ? "var(--warn-soft)" : "var(--surf2)",
      }}
    >
      <span style={{ fontSize: 12.5 }}>
        <b>{previewing ? `If set to ${at} a minute` : `At ${at} a minute`}:</b>{" "}
        {!pace.sends_today ? (
          <>nothing more sends today.</>
        ) : !f || f.texts === 0 ? (
          <>nothing else is waiting to go today.</>
        ) : f.fits_today ? (
          <>
            the {fmt.n(f.texts)} texts waiting today are all sent by about{" "}
            <b>{f.finishes_at ? clock(f.finishes_at) : "—"} ET</b>.
          </>
        ) : (
          <>
            {fmt.n(f.texts - f.left_at_close)} of the {fmt.n(f.texts)} texts waiting today go before the{" "}
            {pace.window.closes} close; <b>{fmt.n(f.left_at_close)}</b> wait for the next sending day.
          </>
        )}
        {pace.replies_an_hour > 0 && (
          <>
            {" "}Expect about <b>{fmt.n(pace.replies_an_hour)} people replying an hour</b> while it runs
            <span className="dim"> ({pace.replies_per_100_texts} per 100 texts over the last 3 days)</span>.
          </>
        )}
      </span>
      {f && f.by_hour.length > 1 && (
        <span className="dim mono" style={{ fontSize: 11.5 }}>
          {f.by_hour.map((h) => `${hourLabel(h.hour)} ${fmt.n(h.texts)}`).join("  ·  ")}
        </span>
      )}
    </div>
  );
}
